import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("React render error:", error, info);
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  reload = (): void => {
    window.location.reload();
  };

  goHome = (): void => {
    window.location.href = "/app";
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message ?? "Unknown error";

    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50 px-6">
        <div className="w-full max-w-md rounded-2xl border border-ink-200 bg-white p-6 shadow-sm">
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-red-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-ink-900">
            Something broke on this page
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            We hit an unexpected error rendering this view. Reloading usually
            fixes it. If it keeps happening, let us know.
          </p>
          <pre className="mt-3 max-h-32 overflow-auto rounded-md bg-ink-50 p-3 text-2xs text-ink-700">
            {message}
          </pre>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={this.reload} className="btn-primary">
              Reload page
            </button>
            <button onClick={this.goHome} className="btn-secondary">
              Back to dashboard
            </button>
            <button onClick={this.reset} className="btn-secondary">
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
